/**
 * Utility functions for Sourcing Manager handling in Site Visit Flow
 */

/**
 * Normalizes and resolves a sourcing manager option with a valid numeric ID if available
 * @param {Object} item - Manager item from API, prefill, or selection
 * @param {Array} managersList - Full list of available sourcing managers
 * @returns {Object|null} Standardized option { label, value, id, uuid }
 */
export const resolveSourcingManagerOption = (item, managersList = []) => {
    if (!item) return null;
    const label = item.name || item.label || `${item.first_name || ''} ${item.last_name || ''}`.trim();
    const rawId = item.id !== undefined && item.id !== null ? item.id : null;
    const rawUuid = item.uuid || item.sourcing_user_uuid || null;

    // Resolve numeric id if possible
    let resolvedId = rawId && !isNaN(rawId) ? Number(rawId) : null;
    if (!resolvedId && rawUuid && Array.isArray(managersList)) {
        const matched = managersList.find((m) => m.uuid === rawUuid || String(m.value) === String(rawUuid) || (m.id && String(m.id) === String(rawUuid)));
        if (matched && matched.id && !isNaN(matched.id)) {
            resolvedId = Number(matched.id);
        }
    }

    return {
        label: label || 'Sourcing Manager',
        value: resolvedId || rawUuid || rawId,
        id: resolvedId,
        uuid: rawUuid
    };
};

/**
 * Validates if the selected sourcing manager has a valid ID or can be resolved against the managers list
 * @param {Object} sourcingManager - Currently selected sourcing manager
 * @param {Array} allSourcingManagers - List of all sourcing managers for project
 * @returns {boolean}
 */
export const isSourcingManagerValid = (sourcingManager, allSourcingManagers = []) => {
    if (!sourcingManager) return false;
    if (sourcingManager.id && !isNaN(sourcingManager.id)) return true;
    if (sourcingManager.value && !isNaN(sourcingManager.value)) return true;
    if (sourcingManager.uuid && Array.isArray(allSourcingManagers)) {
        return allSourcingManagers.some((m) => m.uuid === sourcingManager.uuid && m.id && !isNaN(m.id));
    }
    return false;
};

/**
 * Extracts the final numeric sourcing_manager_id for submission payload
 * @param {Object} sourcingManager - Currently selected sourcing manager
 * @param {Array} allSourcingManagers - List of all sourcing managers for project
 * @returns {number|null}
 */
export const getResolvedSourcingManagerId = (sourcingManager, allSourcingManagers = []) => {
    if (!sourcingManager) return null;
    if (sourcingManager.id && !isNaN(sourcingManager.id)) return Number(sourcingManager.id);
    if (sourcingManager.value && !isNaN(sourcingManager.value)) return Number(sourcingManager.value);
    if (sourcingManager.uuid && Array.isArray(allSourcingManagers)) {
        const matched = allSourcingManagers.find((m) => m.uuid === sourcingManager.uuid || String(m.value) === String(sourcingManager.uuid));
        if (matched && matched.id && !isNaN(matched.id)) return Number(matched.id);
    }
    return null;
};
